import React, { useCallback, useEffect, useRef, useState } from "react";

import Recaptcha from "react-google-recaptcha";

import { RECAPTCHA_SITE_KEY } from "@/constants/envVariables";

import { getAppConfig, AppConfig } from "@/api/getAppConfig";

import { getSdpTenantName } from "@/helpers/getSdpTenantName";
import {
  loadRecaptchaV3Script,
  executeRecaptchaV3,
  resetRecaptchaLoad,
} from "@/helpers/recaptchaV3";

/** reCAPTCHA site key from app-config, or env fallback when /app-config fails before auth. */
function getEffectiveSiteKey(config: AppConfig | null): string {
  if (config?.captcha_site_key) return config.captcha_site_key;
  return typeof RECAPTCHA_SITE_KEY === "string" ? RECAPTCHA_SITE_KEY : "";
}

export const useCaptcha = (recaptchaRef: React.RefObject<Recaptcha | null>) => {
  const lastFetchedOrgName = useRef<string | null>(null);

  const [captchaConfig, setCaptchaConfig] = useState<AppConfig | null>(null);
  const [captchaConfigLoading, setCaptchaConfigLoading] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState("");
  const [recaptchaLoadError, setRecaptchaLoadError] = useState<string | null>(null);

  const siteKey = getEffectiveSiteKey(captchaConfig);
  const isCaptchaDisabled = captchaConfig?.captcha_disabled === true;
  // Only use v3 when backend explicitly says so. Default to v2 when config is missing.
  const isV3 =
    Boolean(captchaConfig) &&
    !isCaptchaDisabled &&
    captchaConfig?.captcha_type === "GOOGLE_RECAPTCHA_V3";
  const isV2 =
    (Boolean(captchaConfig) && !isCaptchaDisabled && !isV3) || (!captchaConfig && !!siteKey);

  const fetchCaptchaConfig = useCallback(async (orgName: string) => {
    if (orgName === lastFetchedOrgName.current) {
      return;
    }

    setCaptchaConfigLoading(true);

    try {
      const config = await getAppConfig();
      lastFetchedOrgName.current = orgName;
      setCaptchaConfig(config);
    } catch {
      lastFetchedOrgName.current = null;
      setCaptchaConfig(null);
    } finally {
      setCaptchaConfigLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCaptchaConfig(getSdpTenantName());
  }, [fetchCaptchaConfig]);

  // Load v3 script when needed. V2 uses the react-google-recaptcha component.
  useEffect(() => {
    if (!isV3 || !siteKey) {
      setRecaptchaLoadError(null);
      return;
    }
    setRecaptchaLoadError(null);
    loadRecaptchaV3Script(siteKey).catch((err) => {
      const message = err?.message || "Failed to load reCAPTCHA v3 script";
      setRecaptchaLoadError(message);
    });
  }, [isV3, siteKey]);

  const onRecaptchaV2Change = (token: string | null) => {
    if (token) {
      setRecaptchaToken(token);
    }
  };

  const onOrgNameChange = (orgName: string) => {
    if (orgName !== lastFetchedOrgName.current) {
      lastFetchedOrgName.current = null;
      setCaptchaConfig(null);
      setRecaptchaToken("");
    }
  };

  const onOrgNameBlur = (orgName: string) => {
    if (orgName) {
      fetchCaptchaConfig(orgName);
    }
  };

  /** Get the recaptcha token for form submission. For v3, executes at call time. */
  const getToken = async (action: string): Promise<string> => {
    if (isCaptchaDisabled) {
      return "";
    }

    if (isV3) {
      if (recaptchaLoadError) {
        throw new Error(recaptchaLoadError);
      }
      return executeRecaptchaV3(siteKey, action);
    }

    return recaptchaToken;
  };

  const retryRecaptchaLoad = useCallback(() => {
    if (!isV3 || !siteKey) return;
    setRecaptchaLoadError(null);
    resetRecaptchaLoad();
    loadRecaptchaV3Script(siteKey).catch((err) => {
      setRecaptchaLoadError(err?.message || "Failed to load reCAPTCHA v3 script");
    });
  }, [isV3, siteKey]);

  const resetCaptcha = () => {
    if (isV2) {
      recaptchaRef.current?.reset();
    }
    setRecaptchaToken("");
  };

  const isPending = captchaConfigLoading || (isV2 && !recaptchaToken);

  return {
    isV2,
    isV3,
    isCaptchaDisabled,
    isPending,
    captchaConfigLoading,
    recaptchaToken,
    recaptchaLoadError,
    retryRecaptchaLoad,
    siteKey,
    onRecaptchaV2Change,
    onOrgNameChange,
    onOrgNameBlur,
    getToken,
    resetCaptcha,
  };
};
